import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../layouts/Layout';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { DashboardFilters } from '../components/DashboardFilters';
import { DatePickerCard } from '../components/DatePickerCard';
import { FollowupModal } from '../components/FollowupModal';
import { SleepStatusModal } from '../components/SleepStatusModal';
import { AnomalyModal } from '../components/AnomalyModal';
import { OfflineDevicesModal } from '../components/OfflineDevicesModal';
import type { AnomalyRow } from '../components/AnomalyModal';
import { useDevices } from '../hooks/useDevices';
import { api } from '../utils/api';
import { getTodayDate } from '../utils/dateUtils';
import { exportToExcel } from '../utils/excelExport';
import type { SleepData, Health, Company, Group, Device, ScheduleType, UserShift, EmployeeFollowup, CreateEmployeeFollowupRequest } from '../types';

export default function Dashboard() {
  const navigate = useNavigate();
  const { devices } = useDevices();
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  
  const [sleepRecordsCount, setSleepRecordsCount] = useState(0);
  const [normalSleepCount, setNormalSleepCount] = useState(0);
  const [abnormalSleepCount, setAbnormalSleepCount] = useState(0);
  const [noDataSleepCount, setNoDataSleepCount] = useState(0);
  const [minSleepDuration, setMinSleepDuration] = useState<number>(360); // Default 6 hours in minutes

  // Helper function to convert stress level number to text
  const getStressLevelText = (value: number): string => {
    if (value === 0) return 'Relax';
    if (value <= 30) return 'Relaxed';
    if (value <= 50) return 'Low Stress';
    if (value < 75) return 'Medium Stress';
    return 'High Stress';
  };

  // Helper function to convert minutes to hours format
  const formatMinutesToHours = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${mins}m`;
  };

  // Helper function to get stress level color
  const getStressLevelColor = (value: number): string => {
    if (value === 0) return 'text-green-600';
    if (value <= 30) return 'text-green-600';
    if (value <= 50) return 'text-yellow-600';
    if (value < 75) return 'text-orange-600';
    return 'text-red-600';
  };

  // Helper function to get sleep status text
  const getSleepStatusText = (sleepTimeSeconds: number): string => {
    const minSleepInSeconds = minSleepDuration * 60; // Convert minutes to seconds
    if (sleepTimeSeconds === 0) return 'No Sleep Data';
    if (sleepTimeSeconds >= minSleepInSeconds) return 'Normal Sleep';
    return 'Abnormal Sleep';
  };

  // Helper function to get sleep status color
  const getSleepStatusColor = (sleepTimeSeconds: number): string => {
    const minSleepInSeconds = minSleepDuration * 60;
    if (sleepTimeSeconds === 0) return 'text-white px-2 py-1 rounded bg-gray-800';
    if (sleepTimeSeconds >= minSleepInSeconds) return 'text-green-600';
    return 'text-red-600';
  };

  // Helper function to check if card should have warning background
  const isAbnormalSleep = (sleepTimeSeconds: number): boolean => {
    const minSleepInSeconds = minSleepDuration * 60;
    return sleepTimeSeconds > 0 && sleepTimeSeconds < minSleepInSeconds;
  };

  // Group management state
  const [companies, setCompanies] = useState<Company[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [sleepDataMap, setSleepDataMap] = useState<Map<string, SleepData>>(new Map());
  const [healthDataMap, setHealthDataMap] = useState<Map<string, Health[]>>(new Map());
  const [deviceShiftMap, setDeviceShiftMap] = useState<Map<string, ScheduleType>>(new Map());
  const [followupMap, setFollowupMap] = useState<Map<string, EmployeeFollowup>>(new Map());
  
  // Followup modal state
  const [isFollowupModalOpen, setIsFollowupModalOpen] = useState(false);
  const [selectedEmployeeForFollowup, setSelectedEmployeeForFollowup] = useState<{
    idEmployee: string;
    name: string;
  } | null>(null);
  
  // Sleep status modal state
  const [isSleepStatusModalOpen, setIsSleepStatusModalOpen] = useState(false);
  const [sleepStatusFilter, setSleepStatusFilter] = useState<'normal' | 'abnormal' | 'nodata'>('normal');
  
  // Anomaly modal state
  const [isAnomalyModalOpen, setIsAnomalyModalOpen] = useState(false);
  const [anomalyRows, setAnomalyRows] = useState<AnomalyRow[]>([]);
  const [anomalyCount, setAnomalyCount] = useState(0);
  
  // Offline devices modal state
  const [isOfflineDevicesModalOpen, setIsOfflineDevicesModalOpen] = useState(false);
  const [offlineDevicesRows, setOfflineDevicesRows] = useState<Array<{
    idEmployee: string;
    Nama: string;
    Jabatan: string | null;
    schedule_type: string;
    shift_date: string;
    window_start: string;
    window_end: string;
    heart_rate_count: number;
    sleep_motion_count: number;
    blood_oxygen_count: number;
    pressure_count: number;
    is_offline: boolean;
  }>>([]);
  const [offlineDevicesCount, setOfflineDevicesCount] = useState(0);
  
  // Expand/collapse state for unassigned section
  const [expandedUnassigned, setExpandedUnassigned] = useState(false);
  
  // Filter state
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedShift, setSelectedShift] = useState<ScheduleType | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(() => getTodayDate());
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
  const [sortBy, setSortBy] = useState<'sleepTime' | 'heartRate' | 'bloodOxygen' | 'stressLevel'>('sleepTime');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showShiftHint, setShowShiftHint] = useState<boolean>(false);

  // Check localStorage for shift selection on mount
  useEffect(() => {
    const savedShift = localStorage.getItem('dashboard_selected_shift');
    const hasSeenShiftHint = localStorage.getItem('dashboard_shift_hint_seen');
    
    if (savedShift) {
      setSelectedShift(savedShift as ScheduleType);
    } else if (!hasSeenShiftHint) {
      // Show hint if user hasn't selected shift before
      setShowShiftHint(true);
    }
  }, []);

  // Auto-select first device when devices are loaded
  useEffect(() => {
    if (devices.length > 0 && !selectedDeviceId && devices[0]?.id) {
      setSelectedDeviceId(devices[0].id);
    }
  }, [devices, selectedDeviceId]);

  // Fetch companies and groups
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companiesData, groupsData] = await Promise.all([
          api.getAllCompanies(),
          api.getAllGroups(),
        ]);
        setCompanies(companiesData);
        setGroups(groupsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Fetch system parameters (including min sleep duration)
  useEffect(() => {
    const fetchSystemParameters = async () => {
      try {
        const params = await api.getSystemParameters();
        console.log('System Parameters:', params); // Debug
        if (params && params.min_sleep_duration) {
          console.log('Min Sleep Duration from API:', params.min_sleep_duration); // Debug
          setMinSleepDuration(params.min_sleep_duration);
        }
      } catch (error) {
        console.error('Error fetching system parameters:', error);
      }
    };
    fetchSystemParameters();
  }, []);

  // Fetch user shifts for the selected date
  useEffect(() => {
    const fetchUserShifts = async () => {
      if (!selectedDate) return;
      
      try {
        const response = await api.getUserShiftsByDate(selectedDate);
        
        if (response && response.all) {
          // Create a map of device_id to schedule_type
          // API returns shifts with idEmployee, so we need to map them to device_id
          const shiftMap = new Map<string, ScheduleType>();
          
          response.all.forEach((shift: UserShift) => {
            // Find device with matching idEmployee
            if (shift.idEmployee) {
              const matchingDevice = devices.find(d => d.idEmployee === shift.idEmployee);
              if (matchingDevice) {
                shiftMap.set(matchingDevice.id, shift.schedule_type);
              }
            }
          });
          
          setDeviceShiftMap(shiftMap);
        }
      } catch (error) {
        console.error('Error fetching user shifts:', error);
        setDeviceShiftMap(new Map());
      }
    };
    
    fetchUserShifts();
  }, [selectedDate, devices]);

  // Fetch followups for the selected date
  useEffect(() => {
    const fetchFollowups = async () => {
      if (!selectedDate) return;
      
      try {
        const followups = await api.getFollowupsByDate(selectedDate);
        const followupMapData = new Map<string, EmployeeFollowup>();
        
        Object.entries(followups).forEach(([idEmployee, followup]) => {
          followupMapData.set(idEmployee, followup as EmployeeFollowup);
        });
        
        setFollowupMap(followupMapData);
      } catch (error) {
        console.error('Error fetching followups:', error);
        setFollowupMap(new Map());
      }
    };
    
    fetchFollowups();
  }, [selectedDate]);

  // Fetch sleep data and statistics for all devices based on selected date
  // This single effect replaces two separate API calls to prevent duplicate requests
  useEffect(() => {
    const fetchSummaryHealthData = async () => {
      if (devices.length === 0) return;
      
      try {
        // Use the new summary-health endpoint - SINGLE API CALL
        const response = await api.getSummaryHealth({
          page: 1,
          limit: 100, // Get all devices
          date: selectedDate
        });
        
        if (response.success && response.data) {
          // Map sleep data
          const sleepMap = new Map<string, SleepData>();
          // Map health data
          const healthMap = new Map<string, Health[]>();
          
          // Statistics counters
          const minSleepInSeconds = minSleepDuration * 60; // Convert minutes to seconds
          let normal = 0;
          let abnormal = 0;
          let noData = 0;
          
          response.data.devices.forEach((deviceSummary: any) => {
            // Find matching device from devices array to get the correct device.id
            const matchingDevice = devices.find(d => d.id === deviceSummary.deviceId);
            const deviceKey = matchingDevice?.id || deviceSummary.deviceId;
            
            // Map sleep data
            if (deviceSummary.sleepData) {
              sleepMap.set(deviceKey, {
                deviceId: deviceSummary.deviceId,
                device_id: deviceSummary.deviceId,
                date: selectedDate,
                sleepTime: deviceSummary.sleepData.sleepTime,
                sleepQuality: deviceSummary.sleepData.sleepQuality / 100, // Convert from 0-100 to 0-1
                sleepTotalTime: deviceSummary.sleepData.sleepTime,
                originalSleepTime: deviceSummary.sleepData.sleepTime,
                heartRate: deviceSummary.sleepData.heartRate || { min: 0, max: 0 },
                bloodOxygen: deviceSummary.sleepData.bloodOxygen || { min: 0, max: 0 },
                sleepLogs: [],
                startTime: deviceSummary.sleepData.startTime,
                endTime: deviceSummary.sleepData.endTime
              } as SleepData);
            }
            
            // Map health data (vital signs)
            if (deviceSummary.vitalSigns) {
              const healthRecords: Health[] = [];
              
              if (deviceSummary.vitalSigns.heartRate !== null) {
                healthRecords.push({
                  id: 0,
                  data_type: 'heart_rate',
                  device_id: deviceSummary.deviceId,
                  string_val: deviceSummary.vitalSigns.heartRate,
                  date_created: new Date().toISOString(),
                  date_updated: new Date().toISOString(),
                  status: 'published',
                  deviceTimestamp: new Date().toISOString(),
                  start_time_utc: new Date().toISOString(),
                  end_time_utc: new Date().toISOString()
                } as Health);
              }
              
              if (deviceSummary.vitalSigns.bloodOxygen !== null) {
                healthRecords.push({
                  id: 0,
                  data_type: 'blood_oxygen',
                  device_id: deviceSummary.deviceId,
                  string_val: deviceSummary.vitalSigns.bloodOxygen,
                  date_created: new Date().toISOString(),
                  date_updated: new Date().toISOString(),
                  status: 'published',
                  deviceTimestamp: new Date().toISOString(),
                  start_time_utc: new Date().toISOString(),
                  end_time_utc: new Date().toISOString()
                } as Health);
              }
              
              if (deviceSummary.vitalSigns.stressLevel !== null) {
                healthRecords.push({
                  id: 0,
                  data_type: 'pressure',
                  device_id: deviceSummary.deviceId,
                  string_val: deviceSummary.vitalSigns.stressLevel,
                  date_created: new Date().toISOString(),
                  date_updated: new Date().toISOString(),
                  status: 'published',
                  deviceTimestamp: new Date().toISOString(),
                  start_time_utc: new Date().toISOString(),
                  end_time_utc: new Date().toISOString()
                } as Health);
              }
              
              if (healthRecords.length > 0) {
                healthMap.set(deviceKey, healthRecords);
              }
            }
            
            // Calculate statistics from the same data
            const deviceShift = deviceShiftMap.get(deviceSummary.deviceId);
            
            // Skip devices with "off" shift or no shift
            if (deviceShift && deviceShift !== 'off') {
              const sleepTime = deviceSummary.sleepData?.sleepTime || 0;
              
              if (sleepTime === 0) {
                noData++;
              } else if (sleepTime >= minSleepInSeconds) {
                normal++;
              } else {
                abnormal++;
              }
            }
          });
          
          // Update all state at once
          setSleepDataMap(sleepMap);
          setHealthDataMap(healthMap);
          setSleepRecordsCount(normal + abnormal);
          setNormalSleepCount(normal);
          setAbnormalSleepCount(abnormal);
          setNoDataSleepCount(noData);
        }
      } catch (error) {
        console.error('Error fetching summary health data:', error);
        setSleepRecordsCount(0);
        setNormalSleepCount(0);
        setAbnormalSleepCount(0);
        setNoDataSleepCount(0);
      }
    };
    fetchSummaryHealthData();
  }, [devices, selectedDate, minSleepDuration, deviceShiftMap]);

  // Fetch anomaly detection data
  useEffect(() => {
    const fetchAnomalyData = async () => {
      if (!selectedDate) return;
      try {
        const data = await api.getAnomalyData(selectedDate);
        if (data) {
          setAnomalyRows(data.rows || []);
          setAnomalyCount(data.anomalyCount || 0);
        }
      } catch (error) {
        console.error('Error fetching anomaly data:', error);
        setAnomalyRows([]);
        setAnomalyCount(0);
      }
    };
    fetchAnomalyData();
  }, [selectedDate]);

  // Fetch offline devices data
  useEffect(() => {
    const fetchOfflineDevices = async () => {
      if (!selectedDate) return;
      try {
        const data = await api.getOfflineDevices(selectedDate);
        if (data) {
          setOfflineDevicesRows(data.rows || []);
          setOfflineDevicesCount(data.offlineCount || 0);
        }
      } catch (error) {
        console.error('Error fetching offline devices data:', error);
        setOfflineDevicesRows([]);
        setOfflineDevicesCount(0);
      }
    };
    fetchOfflineDevices();
  }, [selectedDate]);

  // Filter devices based on selected filters and search query
  const filteredDevices = useMemo(() => {
    let filtered = devices;

    // Filter by group
    if (selectedGroupId) {
      filtered = filtered.filter(d => d.group_id === selectedGroupId);
    }

    // Filter by shift - using deviceShiftMap from API
    if (selectedShift) {
      filtered = filtered.filter(d => deviceShiftMap.get(d.id) === selectedShift);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(device => {
        // Search in device name
        const matchesName = device.name?.toLowerCase().includes(query);
        
        // Search in device ID
        const matchesId = device.id.toLowerCase().includes(query);
        
        // Search in employee NIP
        const matchesNIP = device.employee?.NIP?.toLowerCase().includes(query);
        
        // Search in employee name (Nama)
        const matchesEmployeeName = device.employee?.Nama?.toLowerCase().includes(query);
        
        // Search in idEmployee
        const matchesIdEmployee = device.idEmployee?.toLowerCase().includes(query);
        
        return matchesName || matchesId || matchesNIP || matchesEmployeeName || matchesIdEmployee;
      });
    }

    return filtered;
  }, [devices, selectedGroupId, selectedShift, searchQuery, deviceShiftMap]);

  // Group devices by company and group with sorting
  const groupedDevices = useMemo(() => {
    const grouped: { 
      [key: string]: { 
        company: Company | null; 
        groups: { 
          [key: string]: { 
            group: Group | null; 
            devices: Device[] 
          } 
        } 
      } 
    } = {};
    
    filteredDevices.forEach(device => {
      const companyId = device.company_id || 'unassigned';
      const groupId = device.group_id || 'unassigned';
      
      if (!grouped[companyId]) {
        grouped[companyId] = {
          company: companies.find(c => c.id === companyId) || null,
          groups: {}
        };
      }
      
      if (!grouped[companyId].groups[groupId]) {
        grouped[companyId].groups[groupId] = {
          group: groups.find(g => g.id === groupId) || null,
          devices: []
        };
      }
      
      grouped[companyId].groups[groupId].devices.push(device);
    });
    
    // Sort devices based on selected sort option
    Object.keys(grouped).forEach(companyId => {
      Object.keys(grouped[companyId].groups).forEach(groupId => {
        grouped[companyId].groups[groupId].devices.sort((a, b) => {
          let valueA = 0;
          let valueB = 0;
          
          switch (sortBy) {
            case 'sleepTime':
              valueA = sleepDataMap.get(a.id)?.sleepTime || 0;
              valueB = sleepDataMap.get(b.id)?.sleepTime || 0;
              break;
            case 'heartRate': {
              const healthDataA = healthDataMap.get(a.id) || [];
              const healthDataB = healthDataMap.get(b.id) || [];
              const hrA = healthDataA.filter(h => h.data_type === 'heart_rate').map(h => h.string_val);
              const hrB = healthDataB.filter(h => h.data_type === 'heart_rate').map(h => h.string_val);
              valueA = hrA.length > 0 ? hrA.reduce((x, y) => x + y, 0) / hrA.length : 0;
              valueB = hrB.length > 0 ? hrB.reduce((x, y) => x + y, 0) / hrB.length : 0;
              break;
            }
            case 'bloodOxygen': {
              const healthDataA = healthDataMap.get(a.id) || [];
              const healthDataB = healthDataMap.get(b.id) || [];
              const boA = healthDataA.filter(h => h.data_type === 'blood_oxygen').map(h => h.string_val);
              const boB = healthDataB.filter(h => h.data_type === 'blood_oxygen').map(h => h.string_val);
              valueA = boA.length > 0 ? boA.reduce((x, y) => x + y, 0) / boA.length : 0;
              valueB = boB.length > 0 ? boB.reduce((x, y) => x + y, 0) / boB.length : 0;
              break;
            }
            case 'stressLevel': {
              const healthDataA = healthDataMap.get(a.id) || [];
              const healthDataB = healthDataMap.get(b.id) || [];
              const slA = healthDataA.filter(h => h.data_type === 'pressure').map(h => h.string_val);
              const slB = healthDataB.filter(h => h.data_type === 'pressure').map(h => h.string_val);
              valueA = slA.length > 0 ? slA.reduce((x, y) => x + y, 0) / slA.length : 0;
              valueB = slB.length > 0 ? slB.reduce((x, y) => x + y, 0) / slB.length : 0;
              break;
            }
          }
          
          return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
        });
      });
    });
    
    return grouped;
  }, [filteredDevices, companies, groups, sleepDataMap, healthDataMap, sortBy, sortOrder]);

  const handleClearFilters = () => {
    setSelectedGroupId(null);
    setSelectedShift(null);
    setSelectedDate(getTodayDate());
    setSearchQuery('');
    localStorage.removeItem('dashboard_selected_shift');
  };

  const handleShiftChange = (shift: ScheduleType | null) => {
    setSelectedShift(shift);
    if (shift) {
      localStorage.setItem('dashboard_selected_shift', shift);
      localStorage.setItem('dashboard_shift_hint_seen', 'true');
      setShowShiftHint(false);
    } else {
      localStorage.removeItem('dashboard_selected_shift');
    }
  };

  const handleDismissShiftHint = () => {
    setShowShiftHint(false);
    localStorage.setItem('dashboard_shift_hint_seen', 'true');
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  const handleDownloadReport = async () => {
    try {
      await exportToExcel({
        groupedDevices,
        sleepDataMap,
        healthDataMap,
        deviceShiftMap,
        followupMap,
        selectedDate,
        minSleepDuration,
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Gagal mengunduh laporan. Silakan coba lagi.');
    }
  };

  const handleOpenFollowupModal = (device: Device) => {
    if (!device.idEmployee) {
      alert('Device tidak memiliki employee yang terhubung');
      return;
    }
    
    setSelectedEmployeeForFollowup({
      idEmployee: device.idEmployee,
      name: device.employee?.Nama || device.name || 'Unknown',
    });
    setIsFollowupModalOpen(true);
  };

  const handleSaveFollowup = async (data: CreateEmployeeFollowupRequest) => {
    try {
      const savedFollowup = await api.upsertFollowup(data);
      
      // Update the followup map
      setFollowupMap(prev => {
        const newMap = new Map(prev);
        newMap.set(data.idEmployee, savedFollowup);
        return newMap;
      });
      
      alert('Tindak lanjut berhasil disimpan');
    } catch (error) {
      console.error('Error saving followup:', error);
      throw error;
    }
  };

  const getFollowupBadgeColor = (followupType: string): string => {
    switch (followupType) {
      case 'Konseling':
        return 'bg-blue-100 text-blue-800';
      case 'Peringatan':
        return 'bg-yellow-100 text-yellow-800';
      case 'Observasi':
        return 'bg-purple-100 text-purple-800';
      case 'Tindakan Medis':
        return 'bg-red-100 text-red-800';
      case 'Follow Up':
        return 'bg-green-100 text-green-800';
      case 'Normal':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleOpenSleepStatusModal = (status: 'normal' | 'abnormal' | 'nodata') => {
    setSleepStatusFilter(status);
    setIsSleepStatusModalOpen(true);
  };

  return (
    <Layout>
      <Sidebar />
      <Header title="Dashboard" />

      <main className="ml-0 md:ml-64 pt-20 p-6">
        <div className="grid grid-cols-1 gap-4">

          {/* Sleep Monitoring & Vital Signs Summary - 2 Columns */}
          <div className="grid grid-cols-1 gap-4">
            {/* Sleep Monitoring Summary */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Sleep Monitoring</h2>
                <button
                  onClick={() => navigate('/sleep-monitoring')}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Details →
                </button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-8 gap-4">
                <button
                  onClick={() => handleOpenSleepStatusModal('normal')}
                  className="p-4 bg-green-800 rounded-lg hover:bg-green-900 transition-colors cursor-pointer text-left"
                >
                  <p className="text-sm text-gray-100">Normal Sleep</p>
                  <p className="text-2xl font-bold text-white">{normalSleepCount}</p>
                </button>
                <button
                  onClick={() => handleOpenSleepStatusModal('abnormal')}
                  className="p-4 bg-red-600 rounded-lg hover:bg-red-700 transition-colors cursor-pointer text-left"
                >
                  <p className="text-sm text-gray-100">Abnormal Sleep</p>
                  <p className="text-2xl font-bold text-red-50">{abnormalSleepCount}</p>
                </button>
                <button
                  onClick={() => handleOpenSleepStatusModal('nodata')}
                  className="p-4 bg-gray-800 rounded-lg hover:bg-gray-900 transition-colors cursor-pointer text-left"
                >
                  <p className="text-sm text-gray-100">No Data Sleep</p>
                  <p className="text-2xl font-bold text-gray-50">{noDataSleepCount}</p>
                </button>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Sleep Records</p>
                  <p className="text-2xl font-bold text-blue-600">{sleepRecordsCount}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Min Sleep Duration</p>
                  <p className="text-2xl font-bold text-green-600">{formatMinutesToHours(minSleepDuration)}</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-gray-600">Registered Devices</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {devices.filter(device => device.status === 'published').length}
                  </p>
                </div>
                <button
                  onClick={() => setIsOfflineDevicesModalOpen(true)}
                  className="p-4 bg-slate-700 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer text-left"
                >
                  <p className="text-sm text-slate-100">Offline Devices</p>
                  <p className="text-2xl font-bold text-white">{offlineDevicesCount}</p>
                </button>
                <button
                  onClick={() => setIsAnomalyModalOpen(true)}
                  className="p-4 bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors cursor-pointer text-left"
                >
                  <p className="text-sm text-orange-100">Data Anomaly</p>
                  <p className="text-2xl font-bold text-white">{anomalyCount}</p>
                </button>
              </div>
            </div>

            {/* Vital Signs Summary */}
            {/* <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Vital Signs</h2>
                <button
                  onClick={() => navigate('/vital-signs')}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Details →
                </button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Heart Rate</p>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-gray-500">Avg</p>
                      <p className="text-xl font-bold text-red-600">{healthStats.heartRate.avg} bpm</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Max</p>
                      <p className="text-lg font-semibold text-red-500">{healthStats.heartRate.max}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Min</p>
                      <p className="text-lg font-semibold text-red-500">{healthStats.heartRate.min}</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-cyan-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Blood Oxygen</p>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-gray-500">Avg</p>
                      <p className="text-xl font-bold text-cyan-600">{healthStats.bloodOxygen.avg}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Max</p>
                      <p className="text-lg font-semibold text-cyan-500">{healthStats.bloodOxygen.max}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Min</p>
                      <p className="text-lg font-semibold text-cyan-500">{healthStats.bloodOxygen.min}%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}
          </div>

          {/* Date Filter */}
          <DatePickerCard
            onDateChange={handleDateChange}
            initialDate={selectedDate}
          />

          {/* Shift Selection Hint */}
          {showShiftHint && (
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg shadow-md p-4 mb-4 animate-pulse">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="text-blue-800 font-semibold mb-1">Welcome! Please Select a Shift</h4>
                    <p className="text-blue-700 text-sm">
                      To view relevant data, please select a shift filter (Full Day, Day Shift, Night Shift, or Off) from the filters above. Your selection will be saved for future visits.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleDismissShiftHint}
                  className="text-blue-500 hover:text-blue-700 ml-4 flex-shrink-0"
                  title="Dismiss"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Device Cards Section with Filters */}
          <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
              <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                {/* Search Bar */}
                <div className="relative bg-white rounded-lg shadow px-2 py-3">
                  <input
                    type="text"
                    placeholder="Search by device name, ID, or NIP..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
                  />
                  <svg
                    className="absolute left-5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                <DashboardFilters
                  groups={groups}
                  selectedGroupId={selectedGroupId}
                  selectedShift={selectedShift}
                  onGroupChange={setSelectedGroupId}
                  onShiftChange={handleShiftChange}
                  onClearFilters={handleClearFilters}
                />
                
                {/* Sort Controls */}
                <div className="flex gap-2 bg-white rounded-lg shadow px-2 py-3">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="px-3 w-100 sm:w-50 md:w-50 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sleepTime">Sort by Sleep Time</option>
                    <option value="heartRate">Sort by Heart Rate</option>
                    <option value="bloodOxygen">Sort by Blood Oxygen</option>
                    <option value="stressLevel">Sort by Stress Level</option>
                  </select>
                  
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  >
                    {sortOrder === 'asc' ? (
                      <svg fill="#000000" width="20px" height="20px" viewBox="0 0 256 256" id="Flat" xmlns="http://www.w3.org/2000/svg">
                        <path d="M232.48486,96.47107a11.99915,11.99915,0,0,1-16.97021.002L196,76.9613v67.03223a12,12,0,0,1-24,0V76.964L152.48535,96.47888a12.0001,12.0001,0,0,1-16.9707-16.9707l40-40c.019-.019.04-.03467.05908-.05347.26221-.25854.53418-.50634.81885-.74.14306-.11767.29443-.22021.4414-.33008.16651-.12451.32911-.25341.50245-.36914.17187-.11523.35058-.21533.52734-.32055.15967-.09546.31543-.19556.47949-.28345.1792-.0957.36328-.17676.54541-.26294.17285-.0813.34229-.16748.51953-.241.17676-.07324.35742-.13208.53711-.19653.18994-.06811.377-.14087.57129-.2.17969-.0542.36182-.09375.543-.13965.19824-.0498.39355-.10547.5957-.14526.209-.04151.42041-.06616.63086-.09644.17676-.02539.35059-.05884.53028-.07641.39355-.03882.78808-.05982,1.18359-.05982h0l.00928.00049c.39209.00024.78418.02075,1.1748.05933.17578.01733.34571.05.519.075.21436.03051.4292.05566.64209.0979.19678.03906.3877.09326.58106.14184.18652.04663.37451.08789.55908.14356.188.05713.36963.12793.55371.19385.18555.06616.37207.12744.55518.20312.17041.0708.334.15381.50049.23218.189.08862.37939.17285.56445.27173.15625.084.30469.17968.457.27.18408.10962.37012.21411.54981.33422.16455.11011.31884.23316.47753.35083.15528.11548.314.22388.46485.34766.26416.217.51562.44873.76025.687.0376.03662.07862.06763.11572.105l39.999,39.99317A11.99974,11.99974,0,0,1,232.48486,96.47107ZM48,139.99353h71.999a12,12,0,0,0,0-24H48a12,12,0,0,0,0,24Zm0-64h55.999a12,12,0,0,0,0-24H48a12,12,0,0,0,0,24Zm135.999,104H48a12,12,0,0,0,0,24H183.999a12,12,0,0,0,0-24Z"/>
                      </svg>
                    ) : (
                      <svg fill="#000000" width="20px" height="20px" viewBox="0 0 256 256" id="Flat" xmlns="http://www.w3.org/2000/svg">
                        <path d="M232.4834,176.4867l-39.999,39.99316c-.03613.03613-.07617.06641-.1123.102-.24609.23975-.499.47217-.76465.69043-.14453.11914-.29736.22266-.44629.3335-.165.123-.32617.251-.49756.36572-.17236.11572-.35107.21533-.52783.3208-.15967.09521-.31543.19531-.47949.2832-.17871.09571-.3623.17676-.54492.2627-.17285.08154-.34278.168-.52.24121-.17578.07275-.356.13183-.53467.1958-.19043.06836-.37891.1416-.57422.20068-.17822.0542-.35888.09326-.53906.13867-.19922.0503-.39649.10645-.59961.14649-.20752.041-.417.06543-.626.0957-.17822.02539-.35351.05908-.53418.07715-.39355.03858-.78857.05957-1.18359.05957s-.79-.021-1.18359-.05957c-.1792-.01758-.353-.05127-.52979-.07666-.21094-.03027-.42187-.05469-.63135-.09619-.20117-.04-.39648-.0957-.59423-.14551-.18213-.04541-.36475-.08545-.54444-.13965-.19385-.05859-.38086-.13134-.57031-.1997-.17969-.06446-.36084-.12354-.53858-.19678-.17675-.07373-.34619-.15967-.519-.24121-.18213-.08594-.36621-.167-.54492-.2627-.165-.08789-.32129-.18847-.48145-.28418-.17626-.10547-.35449-.20459-.52587-.31933-.17432-.1167-.33789-.24659-.50538-.37159-.146-.10937-.29589-.21093-.438-.32763-.28516-.23389-.55762-.48242-.81983-.74121-.019-.01856-.04-.03418-.05859-.05274l-40-40a12.0001,12.0001,0,0,1,16.9707-16.9707L172,179.02332V111.99353a12,12,0,0,1,24,0v67.03223L215.51465,159.514A12.0001,12.0001,0,0,1,232.4834,176.4867ZM119.999,115.99353H48a12,12,0,0,0,0,24h71.999a12,12,0,0,0,0-24Zm-71.999-40H183.999a12,12,0,0,0,0-24H48a12,12,0,0,0,0,24Zm56,104H48a12,12,0,0,0,0,24h56a12,12,0,0,0,0-24Z"/>
                      </svg>
                    )}
                  </button>
                </div>
                {/* Download Report Button */}
                <button
                  onClick={handleDownloadReport}
                  className="px-4 my-2 py-3 md:py-0 bg-green-700 hover:bg-green-900 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  title="Download report as Excel file"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-4m0 0V8m0 4h4m-4 0H8M4 20h16a2 2 0 002-2V6a2 2 0 00-2-2H4a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Download Report
                </button>
              </div>
              
              {/* View Mode Toggle */}
              <div className="hidden md:flex items-center gap-2 bg-white rounded-lg shadow p-2 w-full sm:w-auto">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    viewMode === 'cards'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Card View"
                >
                  <svg className="w-5 h-5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    viewMode === 'table'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Table View"
                >
                  <svg className="w-5 h-5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Date Context Info */}
            {/* <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm text-blue-800">
                  Menampilkan data untuk tanggal: <span className="font-semibold">{new Date(selectedDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </span>
              </div>
              {selectedDate !== new Date().toISOString().split('T')[0] && (
                <button
                  onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Kembali ke Hari Ini
                </button>
              )}
            </div> */}

            {/* Search Results Indicator */}
            {searchQuery && (
              <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-sm text-blue-800">
                    Found <span className="font-semibold">{filteredDevices.length}</span> device{filteredDevices.length !== 1 ? 's' : ''} matching "{searchQuery}"
                  </span>
                </div>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear Search
                </button>
              </div>
            )}

            {/* Grouped Device Cards/Table */}
            {Object.keys(groupedDevices).length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
                <p className="text-gray-500 mb-2">
                  {searchQuery 
                    ? `No devices found matching "${searchQuery}"` 
                    : 'No devices found matching the selected filters'}
                </p>
                {(searchQuery || selectedGroupId) && (
                  <button
                    onClick={handleClearFilters}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            ) : viewMode === 'cards' ? (
              // CARDS VIEW
              <div className="space-y-6">
                {Object.entries(groupedDevices)
                  .sort(([keyA], [keyB]) => {
                    // Move 'unassigned' to the end
                    if (keyA === 'unassigned') return 1;
                    if (keyB === 'unassigned') return -1;
                    return 0;
                  })
                  .map(([companyId, companyData]) => {
                    const isUnassigned = companyId === 'unassigned';
                    
                    return (
                      <div key={companyId} className="bg-white rounded-lg shadow">
                        {/* Company Header */}
                        <div
                          className={`p-6 ${isUnassigned ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                          onClick={() => isUnassigned && setExpandedUnassigned(!expandedUnassigned)}
                        >
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-800">
                              {companyData.company?.nama || 'Unassigned'}
                            </h3>
                            {isUnassigned && (
                              <svg
                                className={`w-5 h-5 text-gray-500 transition-transform ${expandedUnassigned ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                              </svg>
                            )}
                          </div>
                        </div>

                        {/* Content - Hidden for Unassigned when collapsed */}
                        {(!isUnassigned || expandedUnassigned) && (
                          <div className="px-6 pb-6">
                            {Object.entries(companyData.groups).map(([groupId, groupData]) => (
                              <div key={groupId} className="mb-6 last:mb-0">
                                <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                                  <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                  </svg>
                                  {groupData.group?.name || 'Unassigned Group'}
                                  <span className="ml-2 text-sm text-gray-500">({groupData.devices.length} devices)</span>
                                </h4>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4">
                                  {groupData.devices.map(device => {
                                    const sleepData = sleepDataMap.get(device.id);
                                    const healthData = healthDataMap.get(device.id) || [];
                                    
                                    // Calculate health stats
                                    const heartRateData = healthData.filter(h => h.data_type === 'heart_rate').map(h => h.string_val);
                                    const bloodOxygenData = healthData.filter(h => h.data_type === 'blood_oxygen').map(h => h.string_val);
                                    const stressLevelData = healthData.filter(h => h.data_type === 'pressure').map(h => h.string_val);
                                    
                                    const avgHeartRate = heartRateData.length > 0 
                                      ? Math.round(heartRateData.reduce((a, b) => a + b, 0) / heartRateData.length)
                                      : 0;
                                    const avgBloodOxygen = bloodOxygenData.length > 0
                                      ? Math.round(bloodOxygenData.reduce((a, b) => a + b, 0) / bloodOxygenData.length)
                                      : 0;
                                    const avgStressLevel = stressLevelData.length > 0
                                      ? Math.round(stressLevelData.reduce((a, b) => a + b, 0) / stressLevelData.length)
                                      : 0;
                                    
                                    // Get sleep time in seconds
                                    const sleepTimeSeconds = sleepData?.sleepTime || 0;
                                    const hasAbnormalSleep = isAbnormalSleep(sleepTimeSeconds);
                                    
                                    return (
                                      <div
                                        key={device.id}
                                        className={`border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
                                          hasAbnormalSleep ? 'bg-red-50 border-red-200' : 'bg-white'
                                        }`}
                                      >
                                        <div className="flex items-start justify-between mb-3">
                                          <div className="flex-1">
                                            <h5 className="font-semibold text-gray-800">{device.employee?.Nama || device.name || 'Unnamed'}</h5>
                                            <small className="text-gray-600">{device.employee?.Jabatan || 'Unknown'}</small>
                                            <div className="flex justify-between gap-2">
                                              <p className={`text-xs mt-1 font-medium ${getSleepStatusColor(sleepTimeSeconds)}`}>
                                                {getSleepStatusText(sleepTimeSeconds)}
                                              </p>
                                              {/* Shift Badge */}
                                              {deviceShiftMap.get(device.id) && (
                                                <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full font-medium ${
                                                  deviceShiftMap.get(device.id) === 'fullday' ? 'bg-blue-100 text-blue-800' :
                                                  deviceShiftMap.get(device.id) === 'day' ? 'bg-yellow-100 text-yellow-800' :
                                                  deviceShiftMap.get(device.id) === 'night' ? 'bg-purple-100 text-purple-800' :
                                                  'bg-gray-100 text-gray-800'
                                                }`}>
                                                  {deviceShiftMap.get(device.id) === 'fullday' ? 'Full Day' :
                                                  deviceShiftMap.get(device.id) === 'day' ? 'Day Shift' :
                                                  deviceShiftMap.get(device.id) === 'night' ? 'Night Shift' :
                                                  'Off'}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Sleep Data - Always show */}
                                        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                          <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-600">Total Sleep Time:</span>
                                            <span className={`font-semibold ${sleepTimeSeconds > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                                              {sleepTimeSeconds > 0 ? (() => {
                                                const hours = Math.floor(sleepTimeSeconds / 3600);
                                                const mins = Math.floor((sleepTimeSeconds % 3600) / 60);
                                                return `${hours}h ${mins}m`;
                                              })() : 'No Data'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-600">Sleep Quality:</span>
                                            <span className={`font-semibold ${sleepData?.sleepQuality ? 'text-purple-600' : 'text-gray-400'}`}>
                                              {sleepData?.sleepQuality ? `${Math.round(sleepData.sleepQuality * 10000)}%` : 'No Data'}
                                            </span>
                                          </div>
                                        </div>
                                        
                                        {/* Health Data - Always show */}
                                        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                          <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-600">Heart Rate:</span>
                                            <span className={`font-semibold ${avgHeartRate > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                              {avgHeartRate > 0 ? `${avgHeartRate} bpm` : 'No Data'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-600">Blood Oxygen:</span>
                                            <span className={`font-semibold ${avgBloodOxygen > 0 ? 'text-cyan-600' : 'text-gray-400'}`}>
                                              {avgBloodOxygen > 0 ? `${avgBloodOxygen}%` : 'No Data'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-600">Stress Level:</span>
                                            <span className={`font-semibold ${avgStressLevel > 0 ? getStressLevelColor(avgStressLevel) : 'text-gray-400'}`}>
                                              {avgStressLevel > 0 ? getStressLevelText(avgStressLevel) : 'No Data'}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                          <div className="flex justify-between items-center text-xs">
                                            <span className="text-gray-600">Notes:</span>
                                            {device.idEmployee && followupMap.get(device.idEmployee) ? (
                                              <button
                                                onClick={() => handleOpenFollowupModal(device)}
                                                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getFollowupBadgeColor(followupMap.get(device.idEmployee)!.followup_type)}`}
                                                title={followupMap.get(device.idEmployee)!.notes || 'Click to edit'}
                                              >
                                                {followupMap.get(device.idEmployee)!.followup_type}
                                              </button>
                                            ) : device.idEmployee ? (
                                              <button
                                                onClick={() => handleOpenFollowupModal(device)}
                                                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                                              >
                                                + Follow Up
                                              </button>
                                            ) : (
                                              <span className="text-gray-400 text-xs">-</span>
                                            )}
                                          </div>
                                          <p className='text-xs'>
                                              {device.idEmployee && followupMap.get(device.idEmployee)?.notes ? (
                                                <span className="text-gray-700">{followupMap.get(device.idEmployee)!.notes}</span>
                                              ) : (
                                                <span className="text-gray-400">-</span>
                                              )}
                                            </p>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            ) : (
              // TABLE VIEW - RESPONSIVE
              <div className="space-y-6">
                {Object.entries(groupedDevices)
                  .sort(([keyA], [keyB]) => {
                    // Move 'unassigned' to the end
                    if (keyA === 'unassigned') return 1;
                    if (keyB === 'unassigned') return -1;
                    return 0;
                  })
                  .map(([companyId, companyData]) => {
                    const isUnassigned = companyId === 'unassigned';
                    
                    return (
                  <div key={companyId} className="bg-white rounded-lg shadow overflow-hidden">
                    <div
                      className={`bg-gray-50 px-4 md:px-6 py-4 border-b border-gray-200 ${isUnassigned ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                      onClick={() => isUnassigned && setExpandedUnassigned(!expandedUnassigned)}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {companyData.company?.nama || 'Unassigned'}
                        </h3>
                        {isUnassigned && (
                          <svg
                            className={`w-5 h-5 text-gray-500 transition-transform ${expandedUnassigned ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                        )}
                      </div>
                    </div>
                    
                    {/* Content - Hidden for Unassigned when collapsed */}
                    {(!isUnassigned || expandedUnassigned) && (
                      <>
                    {Object.entries(companyData.groups).map(([groupId, groupData]) => (
                      <div key={groupId}>
                        <div className="bg-gray-100 px-4 md:px-6 py-3 border-b border-gray-200">
                          <h4 className="text-md font-medium text-gray-700 flex items-center">
                            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {groupData.group?.name || 'Unassigned Group'}
                            <span className="ml-2 text-sm text-gray-500">({groupData.devices.length} devices)</span>
                          </h4>
                        </div>
                        
                        {/* Desktop Table */}
                        <div className="hidden md:block overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                          <table className="w-full min-w-max">
                            <thead>
                              <tr className="border-b border-gray-200 bg-gray-50 sticky top-0">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">ID Employee</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Duty</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Shift</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Sleep Status</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Start Time</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">End Time</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Total Sleep</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Sleep Quality</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">HR</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">SpO2</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Stress</th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Action</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase whitespace-nowrap">Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupData.devices.map((device) => {
                                const sleepData = sleepDataMap.get(device.id);
                                const healthData = healthDataMap.get(device.id) || [];
                                const sleepTimeSeconds = sleepData?.sleepTime || 0;
                                
                                const heartRateData = healthData.filter(h => h.data_type === 'heart_rate').map(h => h.string_val);
                                const bloodOxygenData = healthData.filter(h => h.data_type === 'blood_oxygen').map(h => h.string_val);
                                const stressLevelData = healthData.filter(h => h.data_type === 'pressure').map(h => h.string_val);
                                
                                const avgHeartRate = heartRateData.length > 0 
                                  ? Math.round(heartRateData.reduce((a, b) => a + b, 0) / heartRateData.length)
                                  : 0;
                                const avgBloodOxygen = bloodOxygenData.length > 0
                                  ? Math.round(bloodOxygenData.reduce((a, b) => a + b, 0) / bloodOxygenData.length)
                                  : 0;
                                const avgStressLevel = stressLevelData.length > 0
                                  ? Math.round(stressLevelData.reduce((a, b) => a + b, 0) / stressLevelData.length)
                                  : 0;
                                
                                return (
                                  <tr 
                                    key={device.id} 
                                    className={`border-b border-gray-200 hover:bg-blue-50 transition-colors ${
                                      isAbnormalSleep(sleepTimeSeconds) ? 'bg-red-50' : ''
                                    }`}
                                  >
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{device.employee?.NIP || '-'}</td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap flex items-center gap-2">
                                      {device.employee?.Foto ? (
                                        <img 
                                          src={device.employee.Foto} 
                                          alt={device.employee.Nama}
                                          className="w-10 h-10 rounded object-cover"
                                        />
                                      ) : (
                                        <div className="w-10 h-10 rounded bg-gray-300 flex items-center justify-center">
                                          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                          </svg>
                                        </div>
                                      )}
                                      {device.employee?.Nama || device.name || 'Unnamed'}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{device.employee?.Jabatan || '-'}</td>
                                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                                      {deviceShiftMap.get(device.id) ? (
                                        <span className={`inline-block px-2 py-1 text-xs rounded font-medium ${
                                          deviceShiftMap.get(device.id) === 'fullday' ? 'bg-blue-100 text-blue-800' :
                                          deviceShiftMap.get(device.id) === 'day' ? 'bg-yellow-100 text-yellow-800' :
                                          deviceShiftMap.get(device.id) === 'night' ? 'bg-purple-100 text-purple-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {deviceShiftMap.get(device.id) === 'fullday' ? 'Full Day' :
                                           deviceShiftMap.get(device.id) === 'day' ? 'Day Shift' :
                                           deviceShiftMap.get(device.id) === 'night' ? 'Night Shift' :
                                           'Off'}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400 text-xs">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                                      <span className={`font-medium text-xs ${getSleepStatusColor(sleepTimeSeconds)}`}>
                                        {getSleepStatusText(sleepTimeSeconds)}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                                      {sleepData?.startTime ? (() => {
                                        const date = new Date(sleepData.startTime);
                                        const hours = date.getUTCHours().toString().padStart(2, '0');
                                        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
                                        return `${hours}:${minutes}`;
                                      })() : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                                      {sleepData?.endTime ? (() => {
                                        const date = new Date(sleepData.endTime);
                                        const hours = date.getUTCHours().toString().padStart(2, '0');
                                        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
                                        return `${hours}:${minutes}`;
                                      })() : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700 font-semibold text-center whitespace-nowrap">
                                      {sleepTimeSeconds > 0 ? (() => {
                                        const hours = Math.floor(sleepTimeSeconds / 3600);
                                        const mins = Math.round((sleepTimeSeconds % 3600) / 60);
                                        return `${hours}h ${mins}m`;
                                      })() : 'No Data'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                                      {sleepData?.sleepQuality ? `${Math.round(sleepData.sleepQuality * 10000)}%` : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                                      {avgHeartRate > 0 ? `${avgHeartRate}` : '-'} bpm
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 text-center whitespace-nowrap">
                                      {avgBloodOxygen > 0 ? `${avgBloodOxygen}%` : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                                      <span className={`font-medium text-xs ${avgStressLevel > 0 ? getStressLevelColor(avgStressLevel) : 'text-gray-400'}`}>
                                        {avgStressLevel > 0 ? getStressLevelText(avgStressLevel) : '-'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                                      {device.idEmployee && followupMap.get(device.idEmployee) ? (
                                        <button
                                          onClick={() => handleOpenFollowupModal(device)}
                                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getFollowupBadgeColor(followupMap.get(device.idEmployee)!.followup_type)}`}
                                          title={followupMap.get(device.idEmployee)!.notes || 'Click to edit'}
                                        >
                                          {followupMap.get(device.idEmployee)!.followup_type}
                                        </button>
                                      ) : device.idEmployee ? (
                                        <button
                                          onClick={() => handleOpenFollowupModal(device)}
                                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                                        >
                                          + Follow Up
                                        </button>
                                      ) : (
                                        <span className="text-gray-400 text-xs">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-normal max-w-xs">
                                      {device.idEmployee && followupMap.get(device.idEmployee)?.notes ? (
                                        <span className="text-gray-700">{followupMap.get(device.idEmployee)!.notes}</span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Mobile Card View */}
                        <div className="md:hidden">
                          <div className="divide-y divide-gray-200">
                            {groupData.devices.map((device) => {
                              const sleepData = sleepDataMap.get(device.id);
                              const healthData = healthDataMap.get(device.id) || [];
                              const sleepTimeSeconds = sleepData?.sleepTime || 0;
                              
                              const heartRateData = healthData.filter(h => h.data_type === 'heart_rate').map(h => h.string_val);
                              const bloodOxygenData = healthData.filter(h => h.data_type === 'blood_oxygen').map(h => h.string_val);
                              const stressLevelData = healthData.filter(h => h.data_type === 'pressure').map(h => h.string_val);
                              
                              const avgHeartRate = heartRateData.length > 0 
                                ? Math.round(heartRateData.reduce((a, b) => a + b, 0) / heartRateData.length)
                                : 0;
                              const avgBloodOxygen = bloodOxygenData.length > 0
                                ? Math.round(bloodOxygenData.reduce((a, b) => a + b, 0) / bloodOxygenData.length)
                                : 0;
                              const avgStressLevel = stressLevelData.length > 0
                                ? Math.round(stressLevelData.reduce((a, b) => a + b, 0) / stressLevelData.length)
                                : 0;
                              
                              return (
                                <div
                                  key={device.id}
                                  className={`p-4 hover:bg-gray-50 transition-colors ${
                                    isAbnormalSleep(sleepTimeSeconds) ? 'bg-red-50' : ''
                                  }`}
                                >
                                  <div className="flex justify-between items-start mb-3">
                                    <div>
                                      <h5 className="font-semibold text-gray-900">{device.employee?.Nama || device.name || 'Unnamed'}</h5>
                                      <div className="flex justify-between gap-2">
                                        <p className={`text-xs mt-1 font-medium ${getSleepStatusColor(sleepTimeSeconds)}`}>
                                          {getSleepStatusText(sleepTimeSeconds)}
                                        </p>
                                        {/* Shift Badge */}
                                        {deviceShiftMap.get(device.id) && (
                                          <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full font-medium ${
                                            deviceShiftMap.get(device.id) === 'fullday' ? 'bg-blue-100 text-blue-800' :
                                            deviceShiftMap.get(device.id) === 'day' ? 'bg-yellow-100 text-yellow-800' :
                                            deviceShiftMap.get(device.id) === 'night' ? 'bg-purple-100 text-purple-800' :
                                            'bg-gray-100 text-gray-800'
                                          }`}>
                                            {deviceShiftMap.get(device.id) === 'fullday' ? 'Full Day' :
                                            deviceShiftMap.get(device.id) === 'day' ? 'Day Shift' :
                                            deviceShiftMap.get(device.id) === 'night' ? 'Night Shift' :
                                            'Off'}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                      <p className="text-gray-600 text-xs">Start Time</p>
                                      <p className="font-semibold text-gray-900">
                                        {sleepData?.startTime ? (() => {
                                          const date = new Date(sleepData.startTime);
                                          const hours = date.getUTCHours().toString().padStart(2, '0');
                                          const minutes = date.getUTCMinutes().toString().padStart(2, '0');
                                          return `${hours}:${minutes}`;
                                        })() : '-'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-gray-600 text-xs">End Time</p>
                                      <p className="font-semibold text-gray-900">
                                        {sleepData?.endTime ? (() => {
                                          const date = new Date(sleepData.endTime);
                                          const hours = date.getUTCHours().toString().padStart(2, '0');
                                          const minutes = date.getUTCMinutes().toString().padStart(2, '0');
                                          return `${hours}:${minutes}`;
                                        })() : '-'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-gray-600 text-xs">Total Sleep Time</p>
                                      <p className="font-semibold text-gray-900">
                                        {sleepTimeSeconds > 0 ? (() => {
                                          const hours = Math.floor(sleepTimeSeconds / 3600);
                                          const mins = Math.floor((sleepTimeSeconds % 3600) / 60);
                                          return `${hours}h ${mins}m`;
                                        })() : 'No Data'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-gray-600 text-xs">Sleep Quality</p>
                                      <p className="font-semibold text-gray-900">
                                        {sleepData?.sleepQuality ? `${Math.round(sleepData.sleepQuality * 10000)}%` : 'No Data'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-gray-600 text-xs">Heart Rate</p>
                                      <p className="font-semibold text-gray-900">
                                        {avgHeartRate > 0 ? `${avgHeartRate} bpm` : 'No Data'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-gray-600 text-xs">Blood Oxygen</p>
                                      <p className="font-semibold text-gray-900">
                                        {avgBloodOxygen > 0 ? `${avgBloodOxygen}%` : 'No Data'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-gray-600 text-xs">Stress Level</p>
                                      <p className={`font-semibold ${avgStressLevel > 0 ? getStressLevelColor(avgStressLevel) : 'text-gray-400'}`}>
                                        {avgStressLevel > 0 ? getStressLevelText(avgStressLevel) : 'No Data'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                      </>
                    )}
                  </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Followup Modal */}
      {selectedEmployeeForFollowup && (
        <FollowupModal
          isOpen={isFollowupModalOpen}
          onClose={() => {
            setIsFollowupModalOpen(false);
            setSelectedEmployeeForFollowup(null);
          }}
          onSave={handleSaveFollowup}
          idEmployee={selectedEmployeeForFollowup.idEmployee}
          employeeName={selectedEmployeeForFollowup.name}
          date={selectedDate}
          existingFollowup={followupMap.get(selectedEmployeeForFollowup.idEmployee) || null}
        />
      )}

      {/* Sleep Status Modal */}
      <SleepStatusModal
        isOpen={isSleepStatusModalOpen}
        onClose={() => setIsSleepStatusModalOpen(false)}
        statusFilter={sleepStatusFilter}
        devices={devices}
        sleepDataMap={sleepDataMap}
        healthDataMap={healthDataMap}
        deviceShiftMap={deviceShiftMap}
        followupMap={followupMap}
        minSleepDuration={minSleepDuration}
        onOpenFollowup={handleOpenFollowupModal}
        getFollowupBadgeColor={getFollowupBadgeColor}
      />

      {/* Anomaly Modal */}
      <AnomalyModal
        isOpen={isAnomalyModalOpen}
        onClose={() => setIsAnomalyModalOpen(false)}
        rows={anomalyRows}
        anomalyCount={anomalyCount}
      />

      {/* Offline Devices Modal */}
      <OfflineDevicesModal
        isOpen={isOfflineDevicesModalOpen}
        onClose={() => setIsOfflineDevicesModalOpen(false)}
        rows={offlineDevicesRows}
        offlineCount={offlineDevicesCount}
      />
    </Layout>
  );
}
